/**
 * Centralized layout constants for consistent spacing across the application.
 *
 * These values ensure visual consistency between Header, Sidebar, and MainLayout.
 * When modifying these values, all dependent components will update automatically.
 */

// =============================================================================
// HEADER
// =============================================================================

/**
 * App bar (header) height in pixels.
 * 48px (h-12) is a compact, modern height used by apps like Slack and Notion.
 * Previous value was 64px (h-16).
 */
export const HEADER_HEIGHT = 48;

/**
 * Tailwind class for header height.
 * Must match HEADER_HEIGHT value.
 */
export const HEADER_HEIGHT_CLASS = 'h-12';

// =============================================================================
// SIDEBAR
// =============================================================================

/**
 * Sidebar width in pixels (expanded state).
 */
export const SIDEBAR_WIDTH = 240;

/**
 * Sidebar width in pixels (collapsed state - icon rail).
 */
export const SIDEBAR_COLLAPSED_WIDTH = 64;

// =============================================================================
// SINGLE-PANEL SIDEBAR (Module dropdown + nav)
// =============================================================================

/**
 * Sidebar width when collapsed (icons only).
 * w-14 (56px) - compact but touch-friendly.
 */
export const SIDEBAR_PANEL_COLLAPSED_WIDTH = 56;

/**
 * Sidebar width when expanded on hover (showing labels).
 * w-56 (224px) - enough for nav item text + module dropdown.
 */
export const SIDEBAR_PANEL_EXPANDED_WIDTH = 224;

/**
 * @deprecated Use SIDEBAR_PANEL_COLLAPSED_WIDTH instead.
 * Kept for backwards compatibility during migration.
 */
export const MODULE_RAIL_WIDTH = SIDEBAR_PANEL_COLLAPSED_WIDTH;

/**
 * @deprecated Use SIDEBAR_PANEL_COLLAPSED_WIDTH instead.
 * Total sidebar width when collapsed — now a single panel.
 */
export const SIDEBAR_TWO_TIER_COLLAPSED_WIDTH = SIDEBAR_PANEL_COLLAPSED_WIDTH;

// =============================================================================
// MAIN CONTENT PADDING
// =============================================================================

/**
 * Main content top padding that accounts for fixed header.
 * Calculated as: header height (48px) + bottom spacing (16px) = 64px
 */
export const MAIN_CONTENT_TOP_PADDING_CLASS = 'pt-16';

/**
 * Responsive horizontal padding for main content area.
 * Scales with viewport: 16px (mobile) → 24px (sm) → 32px (lg) → 48px (xl)
 *
 * Best practice: More space on larger screens improves readability and reduces
 * the feeling of content being cramped against edges.
 */
export const MAIN_CONTENT_HORIZONTAL_PADDING_CLASS = 'px-4 sm:px-6 lg:px-8 xl:px-12';

/**
 * Responsive vertical padding for page content (non-fullHeight mode).
 */
export const PAGE_CONTENT_VERTICAL_PADDING_CLASS = 'py-4 sm:py-6';

// =============================================================================
// PAGE WIDTH DEFAULTS
// =============================================================================

/**
 * Default max-width for PageContainer.
 * 'full' (no constraint) is the modern approach - let content use available space.
 * Only constrain width for specific content types:
 * - 'lg' for forms, settings, text-heavy pages
 * - 'xl' for dashboards with cards
 */
export const PAGE_DEFAULT_MAX_WIDTH = 'full' as const;

/**
 * Max-width options for specific page types that need constraints.
 * Most pages should use the default (full width).
 */
export const PAGE_MAX_WIDTHS = {
  /** Forms, settings, text-heavy pages - 1024px */
  form: 'lg',
  /** Dashboards with stat cards - 1280px (optional, can use full) */
  dashboard: 'xl',
} as const;

// =============================================================================
// FULL HEIGHT CALCULATIONS
// =============================================================================

/**
 * Layout padding in pixels (top + bottom from MainLayout).
 * Used for fullHeight calculations in PageContainer.
 * 16px top gap (below header) + 24px bottom (pb-6) = 40px
 */
export const LAYOUT_VERTICAL_PADDING = 40;

/**
 * Total offset for full-height content.
 * Accounts for fixed header height + layout padding.
 */
export const FULL_HEIGHT_OFFSET = HEADER_HEIGHT + LAYOUT_VERTICAL_PADDING;

/**
 * The mobile safe-area insets, as CSS values you can drop into an inline style
 * or a `calc()`. They read the `--safe-*` custom properties that `globals.css`
 * defines from `env(safe-area-inset-*)` — that is the ONE place the env()
 * lookup is spelled, and Tailwind's `safe-top` / `safe-bottom` spacing tokens
 * read the same variables, so a class and a constant can never disagree.
 *
 * Reach for these on anything laid out against the VIEWPORT rather than against
 * the document: a `position: fixed` overlay, a Radix dialog/sheet portal, a
 * toast, a floating bar. `html`'s own safe-area padding cannot reach those —
 * the containing block of a fixed element is the viewport, which on an
 * edge-to-edge Android shell runs behind the status bar and the navigation bar.
 * In normal flow the `html` padding already covers it; adding an inset there
 * double-counts. See docs/design-system/ui-mobile-responsive.md § 1b.
 *
 * Both are `0px` on the web and on desktop, so they are safe to spell
 * unconditionally.
 */
export const SAFE_AREA_TOP = 'var(--safe-top, 0px)';
export const SAFE_AREA_BOTTOM = 'var(--safe-bottom, 0px)';

/**
 * `max-height` for a CENTRED overlay (a Radix `Dialog` / `AlertDialog`), leaving
 * a tenth of the viewport as breathing room and clearing both system bars.
 * Pair it with SAFE_AREA_CENTRED_TOP, which moves the centre of the overlay to
 * the centre of the SAFE region — without that the box is centred on the screen
 * and its bottom half still lands in the navigation bar.
 */
export const SAFE_OVERLAY_MAX_HEIGHT = `calc(90dvh - ${SAFE_AREA_TOP} - ${SAFE_AREA_BOTTOM})`;

/**
 * `top` for an overlay centred with `translate-y-[-50%]`: the midpoint of the
 * safe region rather than the midpoint of the screen. Resolves to exactly `50%`
 * wherever both insets are 0, so desktop layout is byte-identical.
 */
export const SAFE_AREA_CENTRED_TOP = `calc(50% + (${SAFE_AREA_TOP} - ${SAFE_AREA_BOTTOM}) / 2)`;

/**
 * Gap between the screen edge and the desktop rounded content panel, in pixels.
 * Mirrors MainLayout's `mt-2 mr-2 mb-2 ml-2` on that panel — change both together.
 * The mobile layout has no such frame; there the header is fixed at the top and
 * grows by the status-bar safe-area inset instead.
 *
 * Anything positioned against the header's BOTTOM edge from outside the layout
 * (a fixed overlay such as EmulationBanner) must add this, since on desktop the
 * header starts at DESKTOP_FRAME_INSET rather than at 0.
 */
export const DESKTOP_FRAME_INSET = 8;

/**
 * `top` value for a fixed overlay that must float just BELOW the app header.
 *
 * The header is HEADER_HEIGHT tall and the global search pill is vertically
 * CENTERED inside it, so an overlay anchored at the top of the viewport sits
 * exactly on top of the search bar no matter how short the overlay is —
 * shrinking the pill does not help, only moving it does. Both floating banners
 * (EmulationBanner, DemoModeBanner) shipped that defect in turn; anything new
 * that floats over app chrome should read this constant rather than re-deriving
 * the arithmetic.
 *
 * The header's bottom edge differs per layout: on desktop MainLayout insets the
 * rounded content panel by DESKTOP_FRAME_INSET, and on mobile the header
 * instead grows by the status-bar safe-area inset (Header.tsx). Adding both
 * plus 4px of air clears either — measured 4px below the header on desktop,
 * 12px on mobile.
 */
export const HEADER_CLEARANCE_TOP = `calc(${HEADER_HEIGHT}px + ${DESKTOP_FRAME_INSET}px + 4px + ${SAFE_AREA_TOP})`;
